import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Calendar, Button, Modal, Form, Input, message, Card, Space, Tag,
  Select, Row, Col, Empty, Segmented, DatePicker, TimePicker, Switch,
  Tooltip, Popconfirm, Typography, Progress, Table
} from 'antd';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/zh-cn';
import ReminderService from '../utils/reminderService';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, ClockCircleOutlined,
  CalendarOutlined, EnvironmentOutlined, AppstoreOutlined, BarsOutlined,
  RedoOutlined, FlagOutlined
} from '@ant-design/icons';

// 配置axios默认URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

// 设置dayjs为中文
dayjs.locale('zh-cn');
dayjs.extend(isoWeek);

const { TextArea } = Input;
const { Text } = Typography;

// 优先级元数据（艾森豪威尔四象限）
const PRIORITY_META = {
  urgent_important: { label: '紧急且重要', color: '#f5222d', bg: 'rgba(245,34,45,0.10)', tag: 'red', icon: '🔥' },
  urgent_not_important: { label: '紧急不重要', color: '#fa8c16', bg: 'rgba(250,140,22,0.10)', tag: 'orange', icon: '⚡' },
  important_not_urgent: { label: '重要不紧急', color: '#1677ff', bg: 'rgba(22,119,255,0.10)', tag: 'blue', icon: '🎯' },
  not_urgent_not_important: { label: '不急不重要', color: '#52c41a', bg: 'rgba(82,196,26,0.10)', tag: 'green', icon: '🌱' },
};
const PRIORITY_OPTIONS = Object.entries(PRIORITY_META).map(([value, m]) => ({ value, label: m.label }));

const parseTags = (str) => (str ? String(str).split(',').map((s) => s.trim()).filter(Boolean) : []);

const todayStr = () => dayjs().format('YYYY-MM-DD');
const isOverdue = (e) => !e.is_completed && e.event_date < todayStr();
const timeText = (e) => {
  if (e.all_day) return '全天';
  if (e.start_time && e.end_time) return `${e.start_time} - ${e.end_time}`;
  if (e.start_time) return `${e.start_time} 起`;
  return '全天';
};

const Time = () => {
  const { initialize } = useAuthStore();
  const [events, setEvents] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [viewMode, setViewMode] = useState('card');
  const [filters, setFilters] = useState({ search: '', priority: 'all', status: 'all', scope: 'all' });
  // 数据范围：month=按当前月（默认，配合日历）; all=全部事件（点「事件总数/逾期未完成」等统计卡片）
  const [dataScope, setDataScope] = useState('month');
  const [allEvents, setAllEvents] = useState([]);
  const [form] = Form.useForm();
  const reminderServiceRef = useRef(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const year = currentMonth.year();
      const month = currentMonth.month() + 1;
      const response = await axios.get(`/time/month/${year}/${month}`);
      if (response.data.success) {
        setEvents(response.data.events);
      }
    } catch (error) {
      console.error('获取时间事件列表失败:', error);
      message.error('获取时间事件列表失败');
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get('/time/stats/overview');
      if (res.data.success) setOverview(res.data.overview);
    } catch (e) { /* ignore */ }
  }, []);

  // 加载全部事件（不限年月）
  const fetchAllEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/time/events/all');
      if (res.data.success) setAllEvents(res.data.events || []);
    } catch (e) {
      message.error('获取全部事件失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 切到「全部事件」范围并加载
  const showAllScope = useCallback(() => { setDataScope('all'); fetchAllEvents(); }, [fetchAllEvents]);
  // 回到「本月」范围
  const backToMonth = () => { setDataScope('month'); setCurrentMonth(dayjs()); };

  useEffect(() => {
    initialize();
    fetchEvents(); // 始终加载当前月（供日历使用）
    if (dataScope === 'all') fetchAllEvents(); // 「全部事件」范围时额外加载全部
    fetchOverview();
  }, [currentMonth, dataScope, initialize, fetchEvents, fetchAllEvents, fetchOverview]);

  // 初始化提醒服务（保留平台联动）
  useEffect(() => {
    if (events.length > 0) {
      if (!reminderServiceRef.current) {
        reminderServiceRef.current = new ReminderService();
      }
      reminderServiceRef.current.startReminderService(events);
    }
  }, [events]);

  const refresh = () => { fetchEvents(); if (dataScope === 'all') fetchAllEvents(); fetchOverview(); };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // 处理表单数据格式（保持出参结构：event_date 为 YYYY-MM-DD 字符串，时间为 HH:mm）
      const formData = {
        ...values,
        event_date: values.event_date
          ? (dayjs.isDayjs(values.event_date) ? values.event_date.format('YYYY-MM-DD') : values.event_date)
          : dayjs().format('YYYY-MM-DD'),
        start_time: values.start_time ? dayjs(values.start_time).format('HH:mm') : null,
        end_time: values.end_time ? dayjs(values.end_time).format('HH:mm') : null,
      };

      if (editingEvent) {
        const response = await axios.put(`/time/events/${editingEvent.id}`, formData);
        if (response.data.success) {
          message.success('时间事件更新成功');
        }
      } else {
        const response = await axios.post('/time/events', formData);
        if (response.data.success) {
          message.success('时间事件添加成功');
        }
      }

      refresh();
      setModalVisible(false);
      setEditingEvent(null);
      form.resetFields();
    } catch (error) {
      if (error?.errorFields) return; // 表单校验错误
      console.error('提交错误:', error);
      message.error('操作失败');
    }
  };

  const openAdd = (date) => {
    setEditingEvent(null);
    form.resetFields();
    form.setFieldsValue({
      event_date: date || dayjs(),
      priority: 'important_not_urgent',
      reminder_enabled: true,
      all_day: false,
    });
    setModalVisible(true);
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    form.setFieldsValue({
      ...event,
      event_date: dayjs(event.event_date),
      start_time: event.start_time ? dayjs(event.start_time, 'HH:mm') : null,
      end_time: event.end_time ? dayjs(event.end_time, 'HH:mm') : null,
      all_day: !!event.all_day,
      reminder_enabled: event.reminder_enabled !== false,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`/time/events/${id}`);
      if (response.data.success) {
        message.success('时间事件删除成功');
        refresh();
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleComplete = async (id, isCompleted) => {
    try {
      const response = await axios.put(`/time/events/${id}`, { is_completed: !isCompleted });
      if (response.data.success) {
        message.success(isCompleted ? '已取消完成' : '已标记完成');
        refresh();
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleDateSelect = (date) => {
    const selectedDateStr = date.format('YYYY-MM-DD');
    const dayEvents = events.filter((event) => {
      let eventDate;
      if (typeof event.event_date === 'string') {
        eventDate = event.event_date;
      } else {
        eventDate = new Date(event.event_date).toISOString().split('T')[0];
      }
      return eventDate === selectedDateStr;
    });
    setSelectedDate(date);
    setSelectedEvents(dayEvents);
    setDetailModalVisible(true);
  };

  const getPriorityColor = (priority) => (PRIORITY_META[priority]?.tag || 'default');
  const getPriorityText = (priority) => (PRIORITY_META[priority]?.label || '未分类');

  const dateCellRender = (date) => {
    if (!date || !events) return null;
    if (date.month() !== currentMonth.month()) return null;

    const dateStr = date.format('YYYY-MM-DD');
    const dayEvents = events.filter((event) => {
      let eventDate;
      if (typeof event.event_date === 'string') {
        eventDate = event.event_date;
      } else {
        eventDate = new Date(event.event_date).toISOString().split('T')[0];
      }
      return eventDate === dateStr;
    });

    if (dayEvents.length === 0) return null;
    return (
      <div style={{ padding: '2px' }}>
        {dayEvents.slice(0, 3).map((event, index) => {
          const meta = PRIORITY_META[event.priority] || PRIORITY_META.important_not_urgent;
          return (
            <div
              key={event.id || index}
              style={{
                marginBottom: '2px',
                fontSize: '11px',
                color: meta.color,
                backgroundColor: meta.bg,
                padding: '1px 5px',
                borderRadius: '4px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textDecoration: event.is_completed ? 'line-through' : 'none',
                opacity: event.is_completed ? 0.55 : 1,
              }}
            >
              {event.title || '无标题'}
            </div>
          );
        })}
        {dayEvents.length > 3 && (
          <div style={{ fontSize: '10px', color: '#8c8c8c' }}>+{dayEvents.length - 3} 更多</div>
        )}
      </div>
    );
  };

  // 客户端筛选（在当前数据范围上做搜索/优先级/状态/范围过滤）
  // dataScope='all' 时基于「全部事件」，否则基于「当前月」
  const filteredEvents = useMemo(() => {
    const base = dataScope === 'all' ? allEvents : events;
    const kw = filters.search.trim().toLowerCase();
    const weekStart = dayjs().startOf('isoWeek');
    const weekEnd = dayjs().endOf('isoWeek');
    return base.filter((e) => {
      if (kw) {
        const hay = `${e.title || ''} ${e.description || ''} ${e.location || ''} ${e.tags || ''}`.toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      if (filters.priority !== 'all' && e.priority !== filters.priority) return false;
      if (filters.status === 'completed' && !e.is_completed) return false;
      if (filters.status === 'pending' && e.is_completed) return false;
      if (filters.status === 'overdue' && !isOverdue(e)) return false;
      if (filters.scope === 'today' && e.event_date !== todayStr()) return false;
      if (filters.scope === 'week') {
        const d = dayjs(e.event_date);
        if (d.isBefore(weekStart, 'day') || d.isAfter(weekEnd, 'day')) return false;
      }
      return true;
    }).sort((a, b) => (a.event_date === b.event_date
      ? String(a.start_time || '').localeCompare(String(b.start_time || ''))
      : a.event_date.localeCompare(b.event_date)));
  }, [events, allEvents, dataScope, filters]);

  const setFilter = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const jumpToNow = () => { setDataScope('month'); setCurrentMonth(dayjs()); };

  const statCards = [
    // 「事件总数/逾期未完成/完成率」是全局统计 → 点击切到「全部事件」范围（不限年月）
    { key: 'total', label: '事件总数', value: overview?.total ?? 0, icon: '📅', color: '#6366F1', onClick: () => { showAllScope(); setFilter({ status: 'all', scope: 'all', priority: 'all' }); } },
    { key: 'today', label: '今日待办', value: overview?.today ?? 0, icon: '☀️', color: '#1677ff', onClick: () => { jumpToNow(); setFilter({ scope: 'today', status: 'all', priority: 'all' }); } },
    { key: 'week', label: '本周事件', value: overview?.this_week ?? 0, icon: '🗓️', color: '#13c2c2', onClick: () => { jumpToNow(); setFilter({ scope: 'week', status: 'all', priority: 'all' }); } },
    { key: 'overdue', label: '逾期未完成', value: overview?.overdue ?? 0, icon: '⚠️', color: '#f5222d', danger: (overview?.overdue ?? 0) > 0, onClick: () => { showAllScope(); setFilter({ status: 'overdue', scope: 'all', priority: 'all' }); } },
    { key: 'done', label: '完成率', value: `${overview?.completion_rate ?? 0}%`, icon: '✅', color: '#52c41a', onClick: () => { showAllScope(); setFilter({ status: 'completed', scope: 'all', priority: 'all' }); } },
  ];

  const renderCard = (event) => {
    const meta = PRIORITY_META[event.priority] || PRIORITY_META.important_not_urgent;
    const overdue = isOverdue(event);
    const tags = parseTags(event.tags);
    return (
      <Col xs={24} sm={12} md={8} lg={6} key={event.id}>
        <div
          className="mod-card"
          style={{
            borderLeft: `4px solid ${meta.color}`,
            opacity: event.is_completed ? 0.7 : 1,
            ...(overdue ? { boxShadow: '0 0 0 1px #ffccc7 inset' } : {}),
          }}
        >
          <div className="mod-card-head">
            <div className="mod-card-icon" style={{ background: meta.bg, color: meta.color }}>{meta.icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Tooltip title={event.title}>
                <div
                  className="mod-card-title"
                  style={{
                    textDecoration: event.is_completed ? 'line-through' : 'none',
                    color: event.is_completed ? '#999' : undefined,
                  }}
                >
                  {event.title}
                </div>
              </Tooltip>
              <div className="mod-card-sub">
                <ClockCircleOutlined style={{ marginInlineEnd: 4 }} />
                {dayjs(event.event_date).format('MM月DD日')} · {timeText(event)}
              </div>
            </div>
          </div>

          <div className="mod-card-body">
            <div className="mod-badges">
              <span className="mod-badge" style={{ color: meta.color, background: meta.bg }}>{meta.label}</span>
              {overdue && <span className="mod-badge mod-badge-danger">逾期</span>}
              {event.is_completed && <span className="mod-badge mod-badge-success">已完成</span>}
              {event.location && <span className="mod-badge"><EnvironmentOutlined /> {event.location}</span>}
            </div>
            {tags.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {tags.map((t) => <Tag key={t} bordered={false} color="geekblue">{t}</Tag>)}
              </div>
            )}
            {event.description && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }} ellipsis={{ tooltip: event.description }}>
                {event.description}
              </Text>
            )}
          </div>

          <div className="mod-card-actions">
            <Button size="small" type="text" icon={<CheckOutlined />} onClick={() => handleComplete(event.id, event.is_completed)}>
              {event.is_completed ? '取消完成' : '完成'}
            </Button>
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => handleEdit(event)}>编辑</Button>
            <Popconfirm title="确定删除这个事件吗？" onConfirm={() => handleDelete(event.id)} okText="删除" cancelText="取消">
              <Button size="small" type="text" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          </div>
        </div>
      </Col>
    );
  };

  const tableColumns = [
    {
      title: '事件', dataIndex: 'title', key: 'title',
      render: (title, e) => (
        <Space>
          <span style={{ fontSize: 16 }}>{(PRIORITY_META[e.priority] || {}).icon || '📅'}</span>
          <span style={{ textDecoration: e.is_completed ? 'line-through' : 'none', color: e.is_completed ? '#999' : undefined }}>
            <Text strong>{title}</Text>
            {e.location ? <Text type="secondary" style={{ fontSize: 12, marginInlineStart: 6 }}><EnvironmentOutlined /> {e.location}</Text> : null}
          </span>
        </Space>
      ),
    },
    { title: '日期', dataIndex: 'event_date', key: 'event_date', width: 120, render: (d) => dayjs(d).format('YYYY-MM-DD') },
    { title: '时间', key: 'time', width: 130, render: (_, e) => timeText(e) },
    {
      title: '优先级', dataIndex: 'priority', key: 'priority', width: 120,
      render: (p) => <Tag color={getPriorityColor(p)}>{getPriorityText(p)}</Tag>,
    },
    {
      title: '状态', key: 'status', width: 100,
      render: (_, e) => (
        e.is_completed
          ? <Tag color="green">已完成</Tag>
          : isOverdue(e) ? <Tag color="red">逾期</Tag> : <Tag>待办</Tag>
      ),
    },
    {
      title: '操作', key: 'action', width: 140, fixed: 'right',
      render: (_, e) => (
        <Space size={0}>
          <Tooltip title={e.is_completed ? '取消完成' : '标记完成'}>
            <Button size="small" type="text" icon={<CheckOutlined style={{ color: e.is_completed ? '#52c41a' : undefined }} />} onClick={() => handleComplete(e.id, e.is_completed)} />
          </Tooltip>
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => handleEdit(e)} />
          <Popconfirm title="确定删除？" onConfirm={() => handleDelete(e.id)} okText="删除" cancelText="取消">
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="mod-page">
      {/* 页头 */}
      <div className="mod-header">
        <div>
          <div className="mod-header-title">时间管理</div>
          <div className="mod-header-subtitle">日程事件与优先级规划 · 逾期提醒、完成率追踪，已接入平台提醒</div>
        </div>
        <Space wrap>
          <Button icon={<RedoOutlined />} onClick={refresh}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openAdd()}>添加事件</Button>
        </Space>
      </div>

      {/* 统计卡 */}
      <div className="mod-stats">
        {statCards.map((s) => (
          <div
            key={s.key}
            className={`mod-stat-card${s.onClick ? ' clickable' : ''}${s.danger ? ' danger' : ''}`}
            onClick={s.onClick}
          >
            <div className="mod-stat-icon" style={{ color: s.color }}>{s.icon}</div>
            <div>
              <div className="mod-stat-value" style={{ color: s.danger ? '#f5222d' : undefined }}>{s.value}</div>
              <div className="mod-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* 完成率进度条 */}
      {overview && overview.total > 0 && (
        <Card className="mod-toolbar" bodyStyle={{ padding: '10px 16px' }} style={{ marginBottom: 12 }}>
          <Row align="middle" gutter={12}>
            <Col flex="none"><Text type="secondary" style={{ fontSize: 12 }}><FlagOutlined /> 总体完成进度</Text></Col>
            <Col flex="auto">
              <Progress
                percent={overview.completion_rate}
                strokeColor={{ '0%': '#6366F1', '100%': '#52c41a' }}
                format={(p) => `${overview.completed}/${overview.total}（${p}%）`}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* 工具栏 */}
      <Card className="mod-toolbar" bodyStyle={{ padding: 12 }}>
        <div className="mod-toolbar-row">
          <Input.Search
            allowClear
            placeholder="搜索标题 / 描述 / 地点 / 标签"
            value={filters.search}
            onChange={(e) => setFilter({ search: e.target.value })}
            style={{ maxWidth: 260, flex: '1 1 200px' }}
          />
          <DatePicker
            picker="month"
            allowClear={false}
            value={currentMonth}
            onChange={(d) => { if (d) { setDataScope('month'); setCurrentMonth(d); } }}
            style={{ width: 130 }}
          />
          <Select value={filters.priority} onChange={(v) => setFilter({ priority: v })} style={{ width: 140 }}
            options={[{ value: 'all', label: '全部优先级' }, ...PRIORITY_OPTIONS]} />
          <Select value={filters.status} onChange={(v) => setFilter({ status: v })} style={{ width: 130 }}
            options={[
              { value: 'all', label: '全部状态' },
              { value: 'pending', label: '待办' },
              { value: 'completed', label: '已完成' },
              { value: 'overdue', label: '逾期' },
            ]} />
          <Select value={filters.scope} onChange={(v) => setFilter({ scope: v })} style={{ width: 120 }}
            options={[
              { value: 'all', label: '整月' },
              { value: 'today', label: '今日' },
              { value: 'week', label: '本周' },
            ]} />
          <div style={{ marginInlineStart: 'auto' }}>
            <Segmented
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'card', icon: <AppstoreOutlined /> },
                { value: 'table', icon: <BarsOutlined /> },
                { value: 'calendar', icon: <CalendarOutlined /> },
              ]}
            />
          </div>
        </div>
      </Card>

      {/* 全部事件范围提示 */}
      {dataScope === 'all' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, padding: '8px 14px', background: '#eef2ff', border: '1px solid #dfe3ff', borderRadius: 10 }}>
          <span style={{ color: '#4f46e5', fontSize: 13 }}>📋 正在显示<b>全部事件</b>（不限年月，共 {filteredEvents.length} 条）</span>
          <Button size="small" onClick={backToMonth} style={{ marginInlineStart: 'auto' }}>返回本月</Button>
        </div>
      )}

      {/* 内容 */}
      {viewMode === 'calendar' ? (
        <Card style={{ borderRadius: 16, marginTop: 12 }} bodyStyle={{ padding: 8 }} loading={loading}>
          <Calendar
            value={currentMonth}
            cellRender={dateCellRender}
            onSelect={handleDateSelect}
            onPanelChange={(date) => { setDataScope('month'); setCurrentMonth(date); }}
          />
        </Card>
      ) : filteredEvents.length === 0 ? (
        <Empty description="当前筛选下暂无事件" style={{ padding: '60px 0' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openAdd()}>添加事件</Button>
        </Empty>
      ) : viewMode === 'card' ? (
        <Row gutter={[14, 14]} style={{ marginTop: 12 }}>
          {filteredEvents.map(renderCard)}
        </Row>
      ) : (
        <Card bodyStyle={{ padding: 0 }} style={{ borderRadius: 12, overflow: 'hidden', marginTop: 12 }}>
          <Table
            rowKey="id"
            loading={loading}
            columns={tableColumns}
            dataSource={filteredEvents}
            pagination={false}
            scroll={{ x: 800 }}
            size="middle"
          />
        </Card>
      )}

      {/* 添加/编辑事件弹窗 */}
      <Modal
        title={editingEvent ? '编辑时间事件' : '添加时间事件'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => { setModalVisible(false); setEditingEvent(null); form.resetFields(); }}
        okText="确定"
        cancelText="取消"
        width={620}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="title" label="事件标题" rules={[{ required: true, message: '请输入事件标题' }]}>
            <Input placeholder="请输入事件标题" allowClear />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="event_date" label="事件日期" rules={[{ required: true, message: '请选择事件日期' }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="priority" label="优先级" rules={[{ required: true, message: '请选择优先级' }]}>
                <Select placeholder="请选择优先级" options={PRIORITY_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="start_time" label="开始时间">
                <TimePicker style={{ width: '100%' }} format="HH:mm" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="end_time" label="结束时间">
                <TimePicker style={{ width: '100%' }} format="HH:mm" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="location" label="地点">
                <Input placeholder="如：会议室 / 线上 / 客户处" allowClear prefix={<EnvironmentOutlined style={{ color: '#bbb' }} />} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="tags" label="标签">
                <Input placeholder="逗号分隔，如：工作,重要" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="事件描述">
            <TextArea rows={3} placeholder="请输入事件描述" allowClear />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item name="all_day" label="全天事件" valuePropName="checked" initialValue={false}>
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="reminder_enabled" label="启用提醒" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 某天事件详情弹窗（日历视图点击） */}
      <Modal
        title={selectedDate ? `${selectedDate.format('YYYY年MM月DD日')} 事件详情` : '事件详情'}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="add" type="primary" icon={<PlusOutlined />} onClick={() => { setDetailModalVisible(false); openAdd(selectedDate); }}>
            为当天添加
          </Button>,
          <Button key="close" onClick={() => setDetailModalVisible(false)}>关闭</Button>,
        ]}
        width={680}
      >
        {selectedEvents.length === 0 ? (
          <Empty description="该日期暂无事件" />
        ) : (
          <Row gutter={[12, 12]}>
            {selectedEvents.map(renderCard)}
          </Row>
        )}
      </Modal>
    </div>
  );
};

export default Time;
